import { Link } from 'react-router-dom';
import { PageShell } from '../components/PageShell';
import { SUPPORT_EMAIL } from '../content';

const UPDATED = '12 March 2025';

export function PrivacyPage() {
  return (
    <PageShell>
      <div className="container">
        <div className="prose legal-head">
          <div className="eyebrow" style={{ marginBottom: 22 }}>Legal · Privacy</div>
          <h1 className="h-display" style={{ fontSize: 'clamp(38px, 5.4vw, 64px)' }}>
            Privacy Policy
          </h1>
          <p className="h-sub" style={{ marginTop: 20 }}>
            Cranio is built to run on your device. We collect as little as we possibly can — no
            account, no tracking profiles, no selling data. This page explains exactly what that
            means.
          </p>
          <div
            className="mono"
            style={{
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              marginTop: 24,
              fontSize: 11,
              letterSpacing: '0.06em',
              color: 'var(--fg-3)',
            }}
          >
            <span>Last updated · {UPDATED}</span>
            <span>Effective · {UPDATED}</span>
          </div>
        </div>

        <div className="legal-layout">
          <nav className="toc">
            <h4>On this page</h4>
            <ol>
              <li><a href="#summary"><span className="n">01</span> Summary</a></li>
              <li><a href="#collect"><span className="n">02</span> What we collect</a></li>
              <li><a href="#use"><span className="n">03</span> How we use it</a></li>
              <li><a href="#device"><span className="n">04</span> On-device data</a></li>
              <li><a href="#purchases"><span className="n">05</span> Purchases</a></li>
              <li><a href="#third"><span className="n">06</span> Third parties</a></li>
              <li><a href="#rights"><span className="n">07</span> Your rights</a></li>
              <li><a href="#children"><span className="n">08</span> Children</a></li>
              <li><a href="#changes"><span className="n">09</span> Changes</a></li>
              <li><a href="#contact"><span className="n">10</span> Contact</a></li>
            </ol>
          </nav>

          <article className="prose">
            <p style={{ fontSize: '15.5px', color: 'var(--fg-1)' }} id="summary">
              <strong>The short version.</strong> Your studies, lighting setups and saved shots
              live on your device. We don&apos;t make you create an account. We don&apos;t build
              advertising profiles, and we never sell your personal data. The only information
              that leaves your device is the minimum needed to run the app, process a purchase,
              or fix a crash — and only when you opt in.
            </p>

            <h2 id="collect"><span className="num">02</span> What we collect</h2>
            <p>We keep collection deliberately narrow. Depending on how you use Cranio, that can include:</p>
            <ul className="bullets">
              <li><strong>Diagnostics &amp; crash logs</strong> — anonymous reports about app stability, only if you leave Apple&apos;s &ldquo;Share with Developers&rdquo; setting on.</li>
              <li><strong>Purchase receipts</strong> — validated through Apple or Google to unlock Cranio Pro. We see that a purchase happened, not your card details.</li>
              <li><strong>Support correspondence</strong> — if you email us, we keep that thread so we can help and follow up.</li>
            </ul>
            <p>We do <strong>not</strong> collect your name, contacts, photos, precise location, or browsing activity. The app has no third-party advertising or analytics SDKs.</p>

            <h2 id="use"><span className="num">03</span> How we use it</h2>
            <p>The limited data above is used only to:</p>
            <ul className="bullets">
              <li>Keep the app stable and fix bugs you run into.</li>
              <li>Deliver and restore the Pro features you&apos;ve paid for.</li>
              <li>Respond when you reach out to support.</li>
            </ul>
            <p>We never use your information for advertising, profiling, or automated decisions that affect you.</p>

            <h2 id="device"><span className="num">04</span> Data that stays on your device</h2>
            <p>
              Your models, lighting rigs, surface finishes, saved poses and exported reference shots
              are stored locally on your iPhone, iPad or Android device. They may be included in
              your encrypted iCloud, Google, or device backup if you&apos;ve enabled that on your
              phone — that backup is governed by Apple&apos;s or Google&apos;s privacy terms, not
              ours. Deleting the app removes this on-device data.
            </p>

            <h2 id="purchases"><span className="num">05</span> Purchases &amp; subscriptions</h2>
            <p>
              Cranio Pro is sold through the Apple App Store and Google Play. Payments, renewals and
              cancellations are handled entirely by Apple or Google under your Apple Account or
              Google Account — we never receive or store your payment-card information. The store
              shares a transaction receipt with us so we can unlock and restore your purchase
              across devices.
            </p>

            <h2 id="third"><span className="num">06</span> Third-party services</h2>
            <p>We rely on a small number of providers, each acting only as a processor on our behalf:</p>
            <ul className="bullets">
              <li><strong>Apple &amp; Google</strong> — app distribution, in-app purchases and optional crash reporting.</li>
              <li><strong>Our email provider</strong> — to receive and reply to support messages you send us.</li>
            </ul>
            <p>We do not sell, rent, or trade personal data with anyone, for any purpose.</p>

            <h2 id="rights"><span className="num">07</span> Your rights</h2>
            <p>
              Depending on where you live, you may have the right to access, correct, export or
              delete the personal data we hold about you, and to object to certain processing.
              Because most data never leaves your device, the fastest way to exercise these rights
              is often to manage your subscription in your phone&apos;s Settings and delete the
              app. For anything we hold — such as support emails — write to us and we&apos;ll act
              on verified requests within 30 days.
            </p>

            <h2 id="children"><span className="num">08</span> Children</h2>
            <p>
              Cranio is a general-audience creative tool and is not directed at children under 13
              (or the minimum age in your country). We do not knowingly collect personal data from
              children. If you believe a child has sent us personal information, contact us and
              we&apos;ll delete it.
            </p>

            <h2 id="changes"><span className="num">09</span> Changes to this policy</h2>
            <p>
              If we update this policy, we&apos;ll revise the date at the top and, for material
              changes, surface a notice in the app or on this site. Continuing to use Cranio after
              an update means you accept the revised policy.
            </p>

            <h2 id="contact"><span className="num">10</span> Contact us</h2>
            <p>
              Questions about privacy or your data? Email{' '}
              <a
                className="inline"
                href={`mailto:${SUPPORT_EMAIL}?subject=Privacy%20question`}
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              or reach the studio through our{' '}
              <Link className="inline" to="/contact">contact page</Link>. We read every message.
            </p>
          </article>
        </div>
      </div>
    </PageShell>
  );
}
